import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, PackageCheck, Camera, X, ImagePlus } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { RETURN_RENTAL } from "../lib/testIds";
import { returnRental, uploadImage } from "../lib/api";

const MAX_NOTE = 1000;
const MAX_PHOTOS = 5;

export const ReturnRentalModal = ({ rental, open, onOpenChange, onReturned }) => {
    const [note, setNote] = useState("");
    const [photos, setPhotos] = useState([]); // [{url, uploading?, error?}]
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setNote("");
            setPhotos([]);
            setSubmitting(false);
        }
    }, [open]);

    if (!rental) return null;

    const uploadingCount = photos.filter((p) => p.uploading).length;
    const uploadedCount = photos.filter((p) => p.url).length;
    const remaining = MAX_PHOTOS - photos.length;
    // At least one "after" photo is required so the lender always has a
    // visual record of the item's condition at return time. Enforced on
    // the server too — see `POST /api/rentals/{id}/return`.
    const canSubmit = uploadedCount >= 1 && uploadingCount === 0;

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []).slice(0, remaining);
        if (!files.length) return;
        // Insert placeholder entries first to lock the slots in render order
        const placeholders = files.map((f) => ({
            key: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
            uploading: true,
            url: null,
        }));
        setPhotos((prev) => [...prev, ...placeholders]);
        await Promise.all(
            files.map(async (file, idx) => {
                const key = placeholders[idx].key;
                try {
                    const { url } = await uploadImage(file);
                    setPhotos((prev) =>
                        prev.map((p) =>
                            p.key === key ? { key, url, uploading: false } : p,
                        ),
                    );
                } catch (err) {
                    toast.error(`Couldn't upload ${file.name}`, {
                        description:
                            err?.response?.data?.detail ||
                            "Try a smaller JPG/PNG (max 8 MB).",
                    });
                    setPhotos((prev) => prev.filter((p) => p.key !== key));
                }
            }),
        );
    };

    const removePhoto = (key) => {
        setPhotos((prev) => prev.filter((p) => p.key !== key));
    };

    const handleSubmit = async () => {
        if (uploadingCount > 0) {
            toast.error("Hang on — photos still uploading");
            return;
        }
        if (uploadedCount === 0) {
            toast.error("Add at least one return photo", {
                description:
                    "A photo of the item's condition at return protects both you and the lender if there's ever a dispute.",
            });
            return;
        }
        try {
            setSubmitting(true);
            const returnPhotos = photos.map((p) => p.url).filter(Boolean);
            const updated = await returnRental(rental.id, {
                conditionNote: note.trim(),
                returnPhotos,
            });
            toast.success("Return submitted");
            onReturned?.(updated);
            onOpenChange(false);
        } catch (err) {
            toast.error("Couldn't mark as returned", {
                description:
                    err?.response?.data?.detail || "Please try again in a moment.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={RETURN_RENTAL.modal}
                className="max-h-[90vh] max-w-md overflow-y-auto rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-7 text-[#2e3220]"
            >
                <DialogTitle className="flex items-center gap-2 font-display text-2xl font-bold text-[#2e3220]">
                    <PackageCheck className="h-6 w-6 text-[#737a3d]" strokeWidth={2.5} />
                    Return item
                </DialogTitle>
                <DialogDescription className="text-sm text-[#5a6142]">
                    Returning{" "}
                    <span className="font-semibold text-[#2e3220]">
                        {rental.item_name}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold text-[#2e3220]">
                        {rental.owner_name || "your neighbor"}
                    </span>
                    . <span className="font-semibold text-[#B56C5A]">At least one &ldquo;after&rdquo; photo is required</span> so the lender has a
                    visual record of the item&apos;s condition. Add a note too if anything&apos;s worth mentioning.
                </DialogDescription>

                {/* Before/After preview */}
                <div className="mt-5 grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-[#2e3220]/10 bg-white/60 p-2">
                        <p className="px-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Before · listing
                        </p>
                        <div className="mt-1 aspect-square overflow-hidden rounded-xl bg-[#E8E2D0]">
                            {rental.item_image ? (
                                <img
                                    src={rental.item_image}
                                    alt="Before"
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center text-[10px] text-[#5a6142]/60">
                                    No listing photo
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="rounded-2xl border border-[#2e3220]/10 bg-white/60 p-2">
                        <p className="px-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            After · {photos.length}/{MAX_PHOTOS}
                            {uploadedCount === 0 && (
                                <span className="ml-1 text-[#B56C5A]">*</span>
                            )}
                        </p>
                        <div className="mt-1 aspect-square overflow-hidden rounded-xl bg-[#E8E2D0]">
                            {photos[0]?.url ? (
                                <img
                                    src={photos[0].url}
                                    alt="After preview"
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-full w-full flex-col items-center justify-center text-[10px] text-[#5a6142]/60">
                                    <Camera className="mb-1 h-5 w-5" />
                                    Add return photos
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Photo strip */}
                {photos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {photos.map((p, i) => (
                            <div
                                key={p.key}
                                className="relative h-16 w-16 overflow-hidden rounded-xl border border-[#2e3220]/10 bg-[#E8E2D0]"
                            >
                                {p.uploading ? (
                                    <div
                                        data-testid={RETURN_RENTAL.photoThumb(i)}
                                        className="flex h-full w-full items-center justify-center"
                                    >
                                        <Loader2 className="h-4 w-4 animate-spin text-[#737a3d]" />
                                    </div>
                                ) : (
                                    <>
                                        <img
                                            data-testid={RETURN_RENTAL.photoThumb(i)}
                                            src={p.url}
                                            alt={`After ${i + 1}`}
                                            className="h-full w-full object-cover"
                                        />
                                        <button
                                            type="button"
                                            data-testid={RETURN_RENTAL.photoRemove(i)}
                                            onClick={() => removePhoto(p.key)}
                                            aria-label="Remove photo"
                                            className="absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#2e3220] text-[#F4F1EA] shadow hover:bg-[#3d4230]"
                                        >
                                            <X className="h-3 w-3" strokeWidth={2.5} />
                                        </button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    data-testid={RETURN_RENTAL.photoUpload}
                    onChange={(e) => {
                        handleFiles(e.target.files);
                        e.target.value = "";
                    }}
                    className="hidden"
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={remaining <= 0 || submitting}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-[#737a3d]/40 bg-white/40 px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#737a3d] hover:bg-[#737a3d]/5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <ImagePlus className="h-4 w-4" strokeWidth={2.5} />
                    {remaining > 0
                        ? `Add return photo${remaining > 1 ? "s" : ""}`
                        : "Max 5 photos reached"}
                </button>

                <div className="mt-5">
                    <label
                        htmlFor="return-note"
                        className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]"
                    >
                        Condition note (optional)
                    </label>
                    <textarea
                        id="return-note"
                        data-testid={RETURN_RENTAL.note}
                        value={note}
                        onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE))}
                        rows={4}
                        placeholder="Returned in great shape — clean, no damage. Or: a wheel feels a bit loose, FYI."
                        className="mt-2 w-full resize-none rounded-2xl border border-[#2e3220]/15 bg-white/70 px-4 py-3 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                    />
                    <p className="mt-1 text-right text-[10px] text-[#5a6142]/70">
                        {note.length}/{MAX_NOTE}
                    </p>
                </div>

                <div className="mt-4 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        data-testid={RETURN_RENTAL.cancel}
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        className="rounded-full border border-[#2e3220]/15 bg-transparent px-5 py-2 text-sm font-semibold text-[#2e3220] hover:bg-[#2e3220]/5 disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid={RETURN_RENTAL.submit}
                        onClick={handleSubmit}
                        disabled={submitting || !canSubmit}
                        className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        {uploadedCount === 0 ? "Add a photo to continue" : "Start return"}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ReturnRentalModal;
